import { api } from '../../lib/api';

export type JMAPAddress = {
	name?: string;
	email?: string;
};

export type JMAPBodyPart = {
	partId: string;
	type: string;
};

export type JMAPBodyValue = {
	value: string;
};

export type JMAPMailbox = {
	id: string;
	name: string;
	role: string;
	parentId?: string;
	totalEmails: number;
	unreadEmails: number;
};

export type EmailRecord = {
	// JMAP live fields
	id?: string | number;
	messageId?: string[];
	receivedAt?: string;
	subject?: string;
	from?: JMAPAddress | JMAPAddress[] | string;
	to?: JMAPAddress[];
	cc?: JMAPAddress[];
	htmlBody?: JMAPBodyPart[];
	textBody?: JMAPBodyPart[];
	bodyValues?: Record<string, JMAPBodyValue>;
	keywords?: Record<string, boolean>;
	// DB stored fields
	from_addr?: string;
	to_addrs?: string[];
	cc_addrs?: string[];
	date?: string;
	body_html?: string;
	body_text?: string;
	inbound?: boolean;
	outbound?: boolean;
	contact_id?: number | null;
	opportunity_id?: number | null;
	mailbox_label?: string | null;
	domain_used?: string | null;
	created_at?: string;
};

export type SendEmailPayload = {
	opportunity_id?: number;
	contact_id?: number;
	from?: string;
	to: string[];
	cc?: string[];
	subject: string;
	body_text?: string;
	body_html?: string;
};

function extractEmailList(response: unknown): EmailRecord[] {
	if (Array.isArray(response)) return response as EmailRecord[];
	const r = response as Record<string, unknown>;
	const list = r['emails'] ?? r['data'] ?? [];
	return Array.isArray(list) ? (list as EmailRecord[]) : [];
}

function extractMailboxList(response: unknown): JMAPMailbox[] {
	if (Array.isArray(response)) return response as JMAPMailbox[];
	const r = response as Record<string, unknown>;
	const list = r['mailboxes'] ?? [];
	return Array.isArray(list) ? (list as JMAPMailbox[]) : [];
}

export const emailApi = {
	listMailboxes: async (): Promise<JMAPMailbox[]> => {
		const resp = await api.get<unknown>('/email/mailboxes');
		return extractMailboxList(resp);
	},

	fetchMailbox: async (role: string, limit = 50): Promise<EmailRecord[]> => {
		const resp = await api.get<unknown>('/email/fetch', { role, limit });
		return extractEmailList(resp);
	},

	listStored: async (params?: {
		opportunity_id?: number;
		contact_id?: number;
		inbound?: boolean;
		outbound?: boolean;
		domain?: string;
		mailbox_label?: string;
		page?: number;
		limit?: number;
	}): Promise<EmailRecord[]> => {
		const resp = await api.get<unknown>('/emails', params as Record<string, string | number | boolean | undefined | null>);
		return extractEmailList(resp);
	},

	getStored: async (id: number | string): Promise<EmailRecord> => {
		return api.get<EmailRecord>(`/emails/${id}`);
	},

	send: async (payload: SendEmailPayload): Promise<{ success: boolean }> => {
		return api.post<{ success: boolean }>('/email/send', payload);
	},

	linkEmail: async (id: number | string, payload: { contact_id?: number; opportunity_id?: number }) => {
		return api.post<{ success: boolean }>(`/emails/${id}/link`, payload);
	},

	getContactEmails: async (contactId: number | string, params?: { limit?: number; offset?: number }): Promise<EmailRecord[]> => {
		const resp = await api.get<unknown>(`/contacts/${contactId}/emails`, params as Record<string, string | number | boolean | undefined | null>);
		return extractEmailList(resp);
	},

	getOpportunityEmails: async (opportunityId: number | string, params?: { limit?: number; offset?: number }): Promise<EmailRecord[]> => {
		const resp = await api.get<unknown>(`/opportunities/${opportunityId}/emails`, params as Record<string, string | number | boolean | undefined | null>);
		return extractEmailList(resp);
	}
};
